/**
 * usePreviewGenerator - хук для генерации OG-превью сайта
 * Рендерит секции в скрытый контейнер и создаёт скриншоты
 */
import { useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import PreviewRenderer from '../components/PreviewRenderer';
import { getThemeStyles, getThemeFontsUrl } from '../lib/themes';

// Размеры для OG Image
const OG_WIDTH = 1200;
const OG_HEIGHT = 630;

// Размеры для скриншотов
const DESKTOP_WIDTH = 1200;
const DESKTOP_HEIGHT = 800;
const MOBILE_WIDTH = 375;
const MOBILE_HEIGHT = 667;

// Ограничение секций для preview
const MAX_PREVIEW_SECTIONS = 4;

/**
 * Ждёт загрузки шрифтов
 */
async function waitForFonts(doc = document) {
  if (doc.fonts && doc.fonts.ready) {
    await doc.fonts.ready;
  }
  await new Promise(resolve => setTimeout(resolve, 100));
}

/**
 * Загружает шрифты темы в документ
 */
async function loadThemeFonts(theme, doc = document) {
  const fontsUrl = getThemeFontsUrl(theme);
  if (!fontsUrl) return;

  const linkId = `preview-fonts-${theme}`;
  if (doc.getElementById(linkId)) return;

  const link = doc.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = fontsUrl;
  doc.head.appendChild(link);

  // Ждём загрузки
  await new Promise(resolve => {
    link.onload = resolve;
    link.onerror = resolve;
    setTimeout(resolve, 2000); // fallback timeout
  });
}

/**
 * Копирует стили в контейнер
 */
function copyStylesToContainer(container) {
  // Копируем все style и link[rel="stylesheet"] элементы
  const styleElements = document.querySelectorAll('style, link[rel="stylesheet"]');
  styleElements.forEach(el => {
    const clone = el.cloneNode(true);
    container.appendChild(clone);
  });
}

/**
 * Рендерит секции в заданном размере и делает скриншот
 */
async function captureAtSize(sections, theme, width, height) {
  // Создаём контейнер
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: -10000px;
    left: -10000px;
    width: ${width}px;
    height: ${height}px;
    overflow: hidden;
    background: white;
    z-index: -1;
  `;
  document.body.appendChild(container);

  // Применяем CSS variables темы
  const themeStyles = getThemeStyles(theme);
  Object.entries(themeStyles).forEach(([key, value]) => {
    container.style.setProperty(key, value);
  });

  // Создаём wrapper для контента
  const contentWrapper = document.createElement('div');
  contentWrapper.style.cssText = `
    width: ${width}px;
    min-height: ${height}px;
    background: var(--color-background, #ffffff);
  `;
  container.appendChild(contentWrapper);

  // Рендерим React компонент
  const root = createRoot(contentWrapper);

  await new Promise((resolve) => {
    root.render(
      <PreviewRenderer
        sections={sections}
        theme={theme}
        style={{ minHeight: height }}
      />
    );
    // Даём время на рендер
    setTimeout(resolve, 300);
  });

  // Ждём шрифты
  await waitForFonts();

  // Добавляем crossorigin ко всем изображениям и перезагружаем их для CORS
  const images = contentWrapper.querySelectorAll('img');
  await Promise.all(Array.from(images).map(img => {
    return new Promise(resolve => {
      if (!img.src) {
        resolve();
        return;
      }
      // Добавляем cache buster чтобы обойти кэш без CORS
      const originalSrc = img.src;
      img.crossOrigin = 'anonymous';
      img.src = originalSrc + (originalSrc.includes('?') ? '&' : '?') + '_cors=1';
      img.onload = resolve;
      img.onerror = resolve;
      setTimeout(resolve, 3000); // fallback
    });
  }));

  await new Promise(resolve => setTimeout(resolve, 200));

  try {
    // Делаем скриншот
    const canvas = await html2canvas(contentWrapper, {
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      scale: 1,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    return canvas;
  } finally {
    // Очищаем
    root.unmount();
    document.body.removeChild(container);
  }
}

/**
 * Рисует прямоугольник с закруглёнными углами
 */
function roundRect(ctx, x, y, width, height, radius, topOnly = false) {
  ctx.beginPath();
  if (topOnly) {
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x, y + height);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
  } else {
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
  }
  ctx.closePath();
}

/**
 * Создаёт финальную композицию OG-картинки
 * Desktop слева, Mobile справа, симметричные отступы ~50px
 */
function composePreview(desktopCanvas, mobileCanvas, options = {}) {
  const { backgroundColor = null } = options; // null = прозрачный

  const canvas = document.createElement('canvas');
  canvas.width = OG_WIDTH;
  canvas.height = OG_HEIGHT;
  const ctx = canvas.getContext('2d');

  // Фон (прозрачный по умолчанию, или заливка если указан цвет)
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, OG_WIDTH, OG_HEIGHT);
  }

  // Отступы и gap
  const padding = 50;
  const gap = 40;

  // Доступное пространство
  const availableW = OG_WIDTH - padding * 2;   // 1100px
  const availableH = OG_HEIGHT - padding * 2;  // 530px

  // === DESKTOP ===
  // Browser frame добавляет: +8 по ширине, +32 по высоте (28 bar + 4 bottom)
  const browserFrameW = 8;
  const browserFrameH = 32;
  const browserBarH = 28;

  // Масштаб desktop чтобы влезть по высоте
  const desktopScale = (availableH - browserFrameH) / DESKTOP_HEIGHT; // ~0.62
  const desktopW = Math.floor(DESKTOP_WIDTH * desktopScale);
  const desktopH = Math.floor(DESKTOP_HEIGHT * desktopScale);

  // === MOBILE ===
  // Phone frame добавляет: +24 по ширине, +48 по высоте
  const phoneFrameW = 24;
  const phoneFrameH = 48;
  const phoneTopPadding = 24;

  // Масштаб mobile чтобы влезть по высоте
  const mobileScale = (availableH - phoneFrameH) / MOBILE_HEIGHT; // ~0.72
  const mobileW = Math.floor(MOBILE_WIDTH * mobileScale);
  const mobileH = Math.floor(MOBILE_HEIGHT * mobileScale);

  // Общая ширина контента
  const totalContentW = (desktopW + browserFrameW) + gap + (mobileW + phoneFrameW);

  // Центрируем по горизонтали
  const startX = padding + (availableW - totalContentW) / 2;

  // === Позиции ===
  const desktopFrameX = startX;
  const desktopX = desktopFrameX + 4; // скрин внутри frame
  const desktopFrameY = padding;
  const desktopY = desktopFrameY + browserBarH; // скрин под browser bar

  const mobileFrameX = desktopFrameX + desktopW + browserFrameW + gap;
  const mobileX = mobileFrameX + 12; // скрин внутри phone frame
  const mobileFrameY = padding;
  const mobileY = mobileFrameY + phoneTopPadding; // скрин под верхнюю часть phone

  // === РИСУЕМ DESKTOP ===
  // Тень
  ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 10;

  // Browser frame
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, desktopFrameX, desktopFrameY, desktopW + browserFrameW, desktopH + browserFrameH, 10);
  ctx.fill();

  ctx.shadowColor = 'transparent';

  // Browser bar
  ctx.fillStyle = '#f8fafc';
  roundRect(ctx, desktopFrameX, desktopFrameY, desktopW + browserFrameW, browserBarH - 4, 10, true);
  ctx.fill();

  // Browser dots
  ctx.fillStyle = '#ef4444';
  ctx.beginPath();
  ctx.arc(desktopFrameX + 16, desktopFrameY + 12, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fbbf24';
  ctx.beginPath();
  ctx.arc(desktopFrameX + 32, desktopFrameY + 12, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#22c55e';
  ctx.beginPath();
  ctx.arc(desktopFrameX + 48, desktopFrameY + 12, 5, 0, Math.PI * 2);
  ctx.fill();

  // Desktop screenshot
  ctx.drawImage(desktopCanvas, desktopX, desktopY, desktopW, desktopH);

  // === РИСУЕМ MOBILE ===
  // Тень
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 35;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 12;

  // Phone frame
  ctx.fillStyle = '#18181b';
  roundRect(ctx, mobileFrameX, mobileFrameY, mobileW + phoneFrameW, mobileH + phoneFrameH, 28);
  ctx.fill();

  ctx.shadowColor = 'transparent';

  // Phone screen
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, mobileX - 2, mobileY - 2, mobileW + 4, mobileH + 4, 6);
  ctx.fill();

  // Dynamic island
  ctx.fillStyle = '#18181b';
  const islandW = 60;
  const islandH = 8;
  roundRect(ctx, mobileX + mobileW / 2 - islandW / 2, mobileY - 14, islandW, islandH, 4);
  ctx.fill();

  // Mobile screenshot
  ctx.drawImage(mobileCanvas, mobileX, mobileY, mobileW, mobileH);

  return canvas;
}

/**
 * Hook для генерации превью
 */
export function usePreviewGenerator() {
  const isGenerating = useRef(false);

  const generatePreview = useCallback(async (sections, theme = 'default') => {
    if (isGenerating.current) {
      console.warn('Preview generation already in progress');
      return null;
    }

    if (!sections || sections.length === 0) {
      console.warn('No sections to generate preview from');
      return null;
    }

    isGenerating.current = true;

    try {
      // Берём только первые N секций для preview (hero + пара секций)
      const previewSections = sections.slice(0, MAX_PREVIEW_SECTIONS);

      // Загружаем шрифты темы
      await loadThemeFonts(theme);

      // Делаем скриншоты в двух размерах
      const [desktopCanvas, mobileCanvas] = await Promise.all([
        captureAtSize(previewSections, theme, DESKTOP_WIDTH, DESKTOP_HEIGHT),
        captureAtSize(previewSections, theme, MOBILE_WIDTH, MOBILE_HEIGHT),
      ]);

      // Композируем финальное изображение
      const finalCanvas = composePreview(desktopCanvas, mobileCanvas);

      // Возвращаем blob
      return new Promise((resolve) => {
        finalCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 0.92);
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      return null;
    } finally {
      isGenerating.current = false;
    }
  }, []);

  return { generatePreview, isGenerating: isGenerating.current };
}

export default usePreviewGenerator;
